import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSearch,
  FaDownload,
  FaPrint,
  FaTrophy,
  FaTimesCircle,
  FaAward,
  FaExclamationTriangle,
} from "react-icons/fa";

const API = "https://nissi-india.com/api/dashboard";

const TenderDashBoardReport = () => {
  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;

  // ✅ localStorage helper
  const getLS = (key) =>
    typeof window !== "undefined" ? localStorage.getItem(key) || "" : "";

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const lsBranchId = getLS("branchId") || getLS("branchid");
  const branchType = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": lsBranchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, lsBranchId, branchType, userId]
  );

  // ✅ URL params (support both branchId + branchid)
  const sp = new URLSearchParams(window.location.search);
  const status = String(sp.get("status") ?? "1"); // "1" success, "0" unsuccess
  const urlBranchId = String(sp.get("branchId") ?? sp.get("branchid") ?? "");
  const branchId = urlBranchId || lsBranchId;

  const isSuccess = status === "1";
  const title = isSuccess
    ? "TENDER SUCCESSFUL REPORT"
    : "TENDER UNSUCCESSFUL REPORT";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ Search
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/tenders`, {
          headers: commonHeaders,
          params: { status, branchId },
        });

        const data = res.data?.data || [];
        setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [status, branchId, commonHeaders]);

  const filteredData = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter(
      (x) =>
        String(x.contractName || "").toLowerCase().includes(q) ||
        String(x.tenderRefNo || "").toLowerCase().includes(q)
    );
  }, [searchTerm, rows]);

  const totalValue = useMemo(() => {
    return filteredData.reduce(
      (sum, r) => sum + (Number(r.contractValue) || 0),
      0
    );
  }, [filteredData]);

  const fmt2 = (n) =>
    new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(n) || 0);

  const downloadPdf = () => window.print();
  const onPrint = () => window.print();

  return (
    <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
      <div
        className={`p-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : "bg-white text-black"
        }`}
      >
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
          <div className="flex items-center gap-3">
            {isSuccess ? (
              <FaTrophy
                className={`text-2xl ${isDark ? "text-yellow-400" : "text-yellow-600"}`}
              />
            ) : (
              <FaExclamationTriangle
                className={`text-2xl ${isDark ? "text-red-400" : "text-red-600"}`}
              />
            )}

            <div>
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                {title}
              </h1>
              <div className={`text-xs mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                {branchId ? `Branch ID: ${branchId}` : ""}
              </div>
              <div className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                FinYear: {finYear || "-"} | Branch Type: {branchType || "-"} | User ID:{" "}
                {userId || "-"}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative">
              <FaSearch
                className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                  isDark ? "text-gray-400" : "text-gray-500"
                }`}
              />
              <input
                type="text"
                placeholder="Search tenders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`pl-10 pr-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                }`}
              />
            </div>

            <button
              onClick={downloadPdf}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg bg-green-500 text-white"
            >
              <FaDownload /> PDF
            </button>

            <button
              onClick={onPrint}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg bg-purple-500 text-white"
            >
              <FaPrint /> Print
            </button>

            <button
              onClick={() => window.close()}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg bg-gray-200 text-gray-800"
            >
              <FaTimesCircle /> Close
            </button>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div
            className={`p-4 rounded-lg border ${
              isDark ? "bg-blue-900/20 border-blue-700" : "bg-blue-50 border-blue-200"
            }`}
          >
            <div className="text-center">
              <h3 className={`text-lg font-semibold ${isDark ? "text-blue-300" : "text-blue-900"}`}>
                Records
              </h3>
              <p className={`text-2xl font-bold ${isDark ? "text-white" : "text-blue-600"}`}>
                {loading ? "..." : filteredData.length}
              </p>
            </div>
          </div>

          <div
            className={`p-4 rounded-lg border ${
              isDark ? "bg-green-900/20 border-green-700" : "bg-green-50 border-green-200"
            }`}
          >
            <div className="text-center">
              <h3 className={`text-lg font-semibold ${isDark ? "text-green-300" : "text-green-900"}`}>
                Total Value
              </h3>
              <p className={`text-xl font-bold ${isDark ? "text-white" : "text-green-600"}`}>
                ₹{fmt2(totalValue)}
              </p>
            </div>
          </div>

          <div
            className={`p-4 rounded-lg border ${
              isDark ? "bg-purple-900/20 border-purple-700" : "bg-purple-50 border-purple-200"
            }`}
          >
            <div className="text-center">
              <h3 className={`text-lg font-semibold ${isDark ? "text-purple-300" : "text-purple-900"}`}>
                {isSuccess ? "Success Rate" : "Status"}
              </h3>
              <p
                className={`text-xl font-bold flex items-center justify-center gap-2 ${
                  isDark ? "text-white" : "text-purple-600"
                }`}
              >
                {isSuccess ? <FaAward /> : <FaExclamationTriangle />}
                {isSuccess ? "100%" : "Unsuccessful"}
              </p>
            </div>
          </div>
        </div>

        {/* Table */}
        <div
          className={`rounded-xl shadow-sm border overflow-hidden ${
            isDark ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={`${buttonClass} text-white`}>
                <tr>
                  <th className="px-6 py-4 text-center font-semibold border-r border-white/20">
                    S.no
                  </th>
                  <th className="px-6 py-4 text-center font-semibold border-r border-white/20">
                    Contract Name
                  </th>
                  <th className="px-6 py-4 text-center font-semibold border-r border-white/20">
                    Tender RefNo
                  </th>
                  <th className="px-6 py-4 text-center font-semibold">
                    Contract Value
                  </th>
                </tr>
              </thead>

              <tbody className={`divide-y ${isDark ? "divide-gray-700" : "divide-gray-200"}`}>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="text-center py-8">
                      Loading...
                    </td>
                  </tr>
                ) : filteredData.length === 0 ? (
                  <tr className={isDark ? "bg-gray-800" : "bg-gray-50"}>
                    <td
                      colSpan={4}
                      className={`text-center py-8 text-sm ${
                        isDark ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      No Records Found
                    </td>
                  </tr>
                ) : (
                  filteredData.map((item, idx) => (
                    <tr key={idx} className={`${isDark ? "bg-gray-800" : "bg-white"}`}>
                      <td className="px-6 py-4 text-center border-r">
                        {item.sno ?? idx + 1}
                      </td>
                      <td className="px-6 py-4 text-left border-r">
                        {item.contractName}
                      </td>
                      <td className="px-6 py-4 text-center border-r font-medium">
                        {item.tenderRefNo}
                      </td>
                      <td className="px-6 py-4 text-right font-semibold">
                        ₹{fmt2(item.contractValue)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>

              <tfoot className={`${buttonClass} text-white`}>
                <tr>
                  <td
                    colSpan="3"
                    className="px-6 py-4 text-center font-bold border-r border-white/20"
                  >
                    TOTAL
                  </td>
                  <td className="px-6 py-4 text-right font-bold">
                    ₹{fmt2(totalValue)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <ToastContainer />
      </div>
    </div>
  );
};

export default TenderDashBoardReport;